import { Message } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { crmCollaborationTaskQueryRootKey } from "../../crm_collaboration_task/config";
import {
  confirmContactAssignment,
  previewContactAssignment,
  revokeContactSharedOwner,
} from "../../crm_object_assignment/repo/crmObjectAssignmentRepo";
import type { ContactAssignmentConfirmResult } from "../../crm_object_assignment/types";
import {
  crmContactWorkbenchDefaultListParams,
  crmContactWorkbenchQueryRootKey,
} from "../config";
import {
  createContactGift,
  createContactWorkbenchCollaborationTask,
  confirmContactWorkbenchSchoolBatchAssignment,
  deleteContactGift,
  fetchContactGiftOptions,
  fetchContactGifts,
  updateContactGift,
  createContactWorkbenchRecord,
  createContactWorkbenchContact,
  deleteContactWorkbenchRecord,
  fetchContactGiftCounts,
  fetchContactWorkbenchDetail,
  fetchContactWorkbenchList,
  fetchContactWorkbenchMetadata,
  fetchContactWorkbenchOwnerOptions,
  fetchContactWorkbenchRecords,
  fetchContactWorkbenchSchoolBatchAssignmentOrganizationScope,
  fetchLatestContactRecordLabels,
  parseContactWorkbenchNlQuery,
  previewContactWorkbenchSchoolBatchAssignment,
  searchContactWorkbenchOrganizations,
  searchContactWorkbenchSchoolBatchAssignmentOrganizations,
  searchContactWorkbenchOrganizationUnits,
  transferContactWorkbenchOwner,
  updateContactTemporaryTaskHandler,
  updateContactWorkbenchDetail,
  withdrawContactTemporaryTask,
} from "../repo/crmContactWorkbenchRepo";
import {
  formatContactWorkbenchError,
  normalizeContactWorkbenchFilters,
} from "../service/crmContactWorkbenchService";
import {
  checkQuickCreateOpportunity,
  createQuickOpportunity,
} from "../../crm_opportunity_workbench/repo/crmOpportunityWorkbenchRepo";
import type { OpportunityQuickCreateCheck } from "../../crm_opportunity_workbench/types";
import type { ContactWorkbenchListFilters } from "../types";
import type {
  ContactGiftMutationPayload,
  ContactWorkbenchListParams,
  ContactWorkbenchNlQueryParseResult,
  ContactWorkbenchRecordCreatePayload,
  ContactWorkbenchSelectionResolvePayload,
  ContactWorkbenchSchoolBatchAssignmentConfirmPayload,
  ContactWorkbenchSchoolBatchAssignmentConfirmResult,
  ContactWorkbenchSchoolBatchAssignmentPreviewResult,
  ContactWorkbenchTaskPoolPayload,
  ContactWorkbenchTaskPoolResult,
  ContactWorkbenchTransferPayload,
  ContactWorkbenchTransferResult,
  ContactWorkbenchUpdatePayload,
} from "../types";

const CONTACT_WORKBENCH_ALL_SELECTION_PAGE_SIZE = 100;
const CONTACT_WORKBENCH_TRANSFER_CHUNK_SIZE = 500;
const CONTACT_WORKBENCH_SHARED_OWNER_CHUNK_SIZE = 500;

type SharedOwnerBatchPayload = {
  contactIds: number[];
  targetUserIds: number[];
  currentUserId: number;
};

type SharedOwnerReplacePayload = {
  contactId: number;
  currentUserIds: number[];
  targetUserIds: number[];
  currentUserId: number;
};

type SharedOwnerBatchResult = ContactAssignmentConfirmResult & {
  contactCount: number;
  targetUserCount: number;
};

type ContactWorkbenchBatchNotice = {
  type: "info" | "success" | "error";
  content: string;
};

type SchoolBatchAssignmentPreviewPayload = {
  orgIds: number[];
};

/** 编排联系人工作台筛选、分页、排序和批量转移动作。 */
export function useCrmContactWorkbenchRuntime(authenticatedUserId?: number) {
  const queryClient = useQueryClient();
  const currentUserId = authenticatedUserId ?? 0;
  const initialUrlState = useMemo(readContactWorkbenchUrlState, []);
  const [draftFilters, setDraftFilters] = useState<ContactWorkbenchListFilters>(
    initialUrlState.filters,
  );
  const [submittedFilters, setSubmittedFilters] = useState<ContactWorkbenchListFilters>(
    initialUrlState.filters,
  );
  const [page, setPage] = useState(initialUrlState.page);
  const [pageSize, setPageSize] = useState(initialUrlState.pageSize);
  const [sortField, setSortField] = useState(initialUrlState.sortField);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    initialUrlState.sortDirection,
  );
  const [selectedContactId, setSelectedContactId] = useState<number>();
  const [quickOpportunityContactId, setQuickOpportunityContactId] = useState<number>();
  const [quickOpportunityCheck, setQuickOpportunityCheck] =
    useState<OpportunityQuickCreateCheck>();
  const [giftContactId, setGiftContactId] = useState<number>();
  const [giftPage, setGiftPage] = useState(1);
  const giftPageSize = 20;
  const [nlQueryParseResult, setNlQueryParseResult] =
    useState<ContactWorkbenchNlQueryParseResult | null>(null);
  const [nlQueryErrorMessage, setNlQueryErrorMessage] = useState("");
  const [sharedOwnerBatchNotice, setSharedOwnerBatchNotice] =
    useState<ContactWorkbenchBatchNotice | null>(null);
  const schoolBatchAssignmentPreviewMutation = useMutation({
    mutationFn: (payload: SchoolBatchAssignmentPreviewPayload) =>
      previewContactWorkbenchSchoolBatchAssignment(payload),
  });
  const schoolBatchAssignmentConfirmMutation = useMutation({
    mutationFn: (payload: ContactWorkbenchSchoolBatchAssignmentConfirmPayload) =>
      confirmContactWorkbenchSchoolBatchAssignment(payload),
    onSuccess: (result, variables) => {
      // 不 await 列表失效：否则二次确认框会一直转圈，Message 在弹窗关闭前就超时消失，看起来像“没提示”。
      if (variables.assignmentMode === "join_task_pool") {
        if (result.updatedCount > 0) {
          Message.success({
            content: `已将 ${result.updatedCount} 位联系人加入电销任务池`,
            duration: 5000,
          });
        } else {
          Message.warning({
            content: "加入电销任务池完成，没有新增联系人（可能已在任务池中）",
            duration: 5000,
          });
        }
        void Promise.allSettled([
          queryClient.invalidateQueries({ queryKey: crmContactWorkbenchQueryRootKey }),
          queryClient.invalidateQueries({ queryKey: crmCollaborationTaskQueryRootKey }),
        ]);
        return;
      }
      if (result.updatedCount > 0) {
        Message.success({
          content: `按学校批量分配完成，已更新 ${result.updatedCount} 条联系人`,
          duration: 5000,
        });
      } else {
        Message.warning({
          content: "按学校批量分配完成，没有需要变更的联系人（可能已是目标销售或不可分配）",
          duration: 5000,
        });
      }
      void queryClient.invalidateQueries({ queryKey: crmContactWorkbenchQueryRootKey });
    },
    onError: (error) => {
      Message.error({
        content: formatContactWorkbenchError(error, "按学校批量分配失败，请稍后重试"),
        duration: 5000,
      });
    },
  });

  const listParams = useMemo(
    () => ({
      ...crmContactWorkbenchDefaultListParams,
      ...submittedFilters,
      page,
      pageSize,
      sortField,
      sortDirection,
    }),
    [page, pageSize, sortDirection, sortField, submittedFilters],
  );

  useEffect(() => {
    writeContactWorkbenchUrlState(listParams);
  }, [listParams]);

  const metadataQuery = useQuery({
    queryKey: [...crmContactWorkbenchQueryRootKey, "metadata", currentUserId],
    queryFn: fetchContactWorkbenchMetadata,
    // 记录类型/form_mode 随版本变更，挂载与切回窗口时强制拉新，避免 5 分钟内仍用旧下拉与表单模式。
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const listQuery = useQuery({
    queryKey: [...crmContactWorkbenchQueryRootKey, "contacts", listParams],
    queryFn: () => fetchContactWorkbenchList(listParams),
  });

  const ownerOptionsQuery = useQuery({
    queryKey: [
      ...crmContactWorkbenchQueryRootKey,
      "owner-options",
      {
        viewScope: submittedFilters.viewScope ?? "mine",
        collaborationScope: submittedFilters.collaborationScope,
        collaborationTaskType: submittedFilters.collaborationTaskType,
        contactStar: submittedFilters.contactStar,
        contactStarUnset: submittedFilters.contactStarUnset,
        recordLabels: submittedFilters.recordLabels,
        followUpLabels: submittedFilters.followUpLabels,
        keyword: submittedFilters.keyword,
        orgId: submittedFilters.orgId,
        orgIds: submittedFilters.orgIds,
        province: submittedFilters.province,
        provinces: submittedFilters.provinces,
        city: submittedFilters.city,
        district: submittedFilters.district,
        isActive: submittedFilters.isActive,
        nlQuery: submittedFilters.nlQuery,
      },
    ],
    queryFn: () =>
      fetchContactWorkbenchOwnerOptions({
        viewScope: submittedFilters.viewScope,
        collaborationScope: submittedFilters.collaborationScope,
        collaborationTaskType: submittedFilters.collaborationTaskType,
        contactStar: submittedFilters.contactStar,
        contactStarUnset: submittedFilters.contactStarUnset,
        recordLabels: submittedFilters.recordLabels,
        followUpLabels: submittedFilters.followUpLabels,
        keyword: submittedFilters.keyword,
        orgId: submittedFilters.orgId,
        orgIds: submittedFilters.orgIds,
        province: submittedFilters.province,
        provinces: submittedFilters.provinces,
        city: submittedFilters.city,
        district: submittedFilters.district,
        isActive: submittedFilters.isActive,
        nlQuery: submittedFilters.nlQuery,
      }),
  });

  const currentPageContactIds = useMemo(
    () => [...new Set((listQuery.data?.items ?? []).map((item) => item.id))].sort((a, b) => a - b),
    [listQuery.data?.items],
  );
  const latestRecordLabelsQuery = useQuery({
    queryKey: [
      ...crmContactWorkbenchQueryRootKey,
      "latest-record-labels",
      currentPageContactIds,
      submittedFilters.viewScope ?? "mine",
    ],
    queryFn: () =>
      submittedFilters.viewScope === "all"
        ? fetchLatestContactRecordLabels(currentPageContactIds, "all")
        : fetchLatestContactRecordLabels(currentPageContactIds),
    enabled: currentPageContactIds.length > 0,
    staleTime: 30_000,
  });
  const giftCountsQuery = useQuery({
    queryKey: [
      ...crmContactWorkbenchQueryRootKey,
      "gift-counts",
      currentPageContactIds,
      submittedFilters.viewScope ?? "mine",
    ],
    queryFn: () =>
      submittedFilters.viewScope === "all"
        ? fetchContactGiftCounts(currentPageContactIds, "all")
        : fetchContactGiftCounts(currentPageContactIds),
    enabled: currentPageContactIds.length > 0 && sortField !== "gift_count",
    staleTime: 30_000,
  });
  const giftCountsByContactId = useMemo(
    () => new Map((giftCountsQuery.data ?? []).map((item) => [item.contactId, item.giftCount])),
    [giftCountsQuery.data],
  );
  const nlQueryParseMutation = useMutation({
    mutationFn: parseContactWorkbenchNlQuery,
    onSuccess: (result) => {
      setNlQueryParseResult(result);
    },
  });

  const transferMutation = useMutation({
    mutationFn: transferContactsOwnerInChunks,
    onSuccess: async (result, variables) => {
      Message.success(`已转移 ${result.updatedCount} 位联系人`);
      const transferredIds = new Set(dedupeContactIds(variables.contactIds));
      setSelectedContactId((current) =>
        current && transferredIds.has(current) ? undefined : current,
      );
      // 先清掉可能仍带旧页联系人 ID 的异步补齐查询，避免转移后瞬时 403 触发全局无权限 toast。
      queryClient.removeQueries({
        queryKey: [...crmContactWorkbenchQueryRootKey, "latest-record-labels"],
      });
      queryClient.removeQueries({
        queryKey: [...crmContactWorkbenchQueryRootKey, "gift-counts"],
      });
      await queryClient.invalidateQueries({ queryKey: crmContactWorkbenchQueryRootKey });
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "转移主销售失败，请稍后重试"));
    },
  });

  const taskPoolMutation = useMutation({
    mutationFn: createSelectedContactsTaskPool,
    onSuccess: (result, variables) => {
      Message.success(
        `已将 ${result.insertedCount} 位联系人加入${formatTaskPoolTypeLabel(variables.taskType)}任务池`,
      );
      void Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: crmContactWorkbenchQueryRootKey }),
        queryClient.invalidateQueries({ queryKey: crmCollaborationTaskQueryRootKey }),
      ]);
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "投放任务池失败，请稍后重试"));
    },
  });

  const sharedOwnerMutation = useMutation({
    mutationFn: addContactsSharedOwnersInChunks,
    onMutate: (variables) => {
      const contactCount = dedupeContactIds(variables.contactIds).length;
      const targetUserCount = dedupeUserIds(variables.targetUserIds).length;
      setSharedOwnerBatchNotice({
        type: "info",
        content: `正在后台设置 ${contactCount} 位联系人、${targetUserCount} 位共享销售`,
      });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: crmContactWorkbenchQueryRootKey });
      setSharedOwnerBatchNotice({
        type: "success",
        content: `共享销售设置完成：${result.contactCount} 位联系人、${result.targetUserCount} 位共享销售，更新 ${result.updatedCount} 条关系`,
      });
    },
    onError: (error) => {
      setSharedOwnerBatchNotice({
        type: "error",
        content: formatContactWorkbenchError(error, "添加共享销售失败，请稍后重试"),
      });
    },
  });

  const replaceSharedOwnerMutation = useMutation({
    mutationFn: replaceContactSharedOwners,
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: crmContactWorkbenchQueryRootKey });
      Message.success(
        `共享销售已更新：新增 ${result.addedCount} 人，移除 ${result.removedCount} 人`,
      );
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "更新共享销售失败，请稍后重试"));
    },
  });

  const temporaryTaskHandlerMutation = useMutation({
    mutationFn: updateContactTemporaryTaskHandler,
    onSuccess: async (assignment) => {
      Message.success(
        assignment.handlerUserName
          ? `临时任务已调整为 ${assignment.handlerUserName}`
          : "临时任务已收回",
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: crmContactWorkbenchQueryRootKey }),
        queryClient.invalidateQueries({ queryKey: crmCollaborationTaskQueryRootKey }),
      ]);
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "更新临时任务处理人失败，请稍后重试"));
    },
  });

  const temporaryTaskWithdrawMutation = useMutation({
    mutationFn: withdrawContactTemporaryTask,
    onSuccess: async () => {
      Message.success("临时任务已收回");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: crmContactWorkbenchQueryRootKey }),
        queryClient.invalidateQueries({ queryKey: crmCollaborationTaskQueryRootKey }),
      ]);
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "收回临时任务失败，请稍后重试"));
    },
  });

  const detailQuery = useQuery({
    queryKey: [...crmContactWorkbenchQueryRootKey, "contact-detail", selectedContactId],
    queryFn: () => fetchContactWorkbenchDetail(selectedContactId as number),
    enabled: Boolean(selectedContactId),
  });

  const recordsQuery = useQuery({
    queryKey: [...crmContactWorkbenchQueryRootKey, "contact-records", selectedContactId],
    queryFn: () => fetchContactWorkbenchRecords(selectedContactId as number),
    enabled: Boolean(selectedContactId),
  });

  const giftsQuery = useQuery({
    queryKey: [
      ...crmContactWorkbenchQueryRootKey,
      "contact-gifts",
      giftContactId,
      giftPage,
      giftPageSize,
    ],
    queryFn: () => fetchContactGifts(giftContactId as number, giftPage, giftPageSize),
    enabled: Boolean(giftContactId),
  });

  const giftOptionsQuery = useQuery({
    queryKey: [...crmContactWorkbenchQueryRootKey, "gift-options"],
    queryFn: fetchContactGiftOptions,
    enabled: Boolean(giftContactId),
  });



  const invalidateGiftQueries = (contactId: number) => {
    void Promise.allSettled([
      queryClient.invalidateQueries({
        queryKey: [...crmContactWorkbenchQueryRootKey, "contact-gifts", contactId],
      }),
      queryClient.invalidateQueries({ queryKey: [...crmContactWorkbenchQueryRootKey, "contacts"] }),
      queryClient.invalidateQueries({ queryKey: [...crmContactWorkbenchQueryRootKey, "gift-counts"] }),
    ]);
  };
  const createGiftMutation = useMutation({
    mutationFn: ({ contactId, payload }: { contactId: number; payload: ContactGiftMutationPayload }) =>
      createContactGift(contactId, payload),
    onSuccess: (_, variables) => {
      Message.success("礼品记录已新增");
      invalidateGiftQueries(variables.contactId);
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "礼品新增失败，请稍后重试"));
    },
  });

  const updateGiftMutation = useMutation({
    mutationFn: ({ contactId, giftId, payload }: {
      contactId: number;
      giftId: string;
      payload: ContactGiftMutationPayload;
    }) => updateContactGift(contactId, giftId, payload),
    onSuccess: (_, variables) => {
      Message.success("礼品记录已更新");
      invalidateGiftQueries(variables.contactId);
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "礼品更新失败，请稍后重试"));
    },
  });
  const deleteGiftMutation = useMutation({
    mutationFn: ({ contactId, giftId }: { contactId: number; giftId: string }) =>
      deleteContactGift(contactId, giftId),
    onSuccess: (_, variables) => {
      Message.success("删除成功");
      invalidateGiftQueries(variables.contactId);
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "礼品删除失败，请稍后重试"));
    },
  });
  const updateContactMutation = useMutation({
    mutationFn: updateContactWorkbenchDetail,
    onSuccess: (detail, variables) => {
      queryClient.setQueryData(
        [...crmContactWorkbenchQueryRootKey, "contact-detail", variables.contactId],
        detail,
      );
      if (detail.vectorSyncWarning) Message.warning({ content: detail.vectorSyncWarning, duration: 8000 });
      else Message.success("联系人资料已保存");
      void Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: [...crmContactWorkbenchQueryRootKey, "contact-records", variables.contactId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...crmContactWorkbenchQueryRootKey, "latest-record-labels"],
        }),
        queryClient.invalidateQueries({
          queryKey: [...crmContactWorkbenchQueryRootKey, "contacts"],
        }),
      ]);
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "联系人资料保存失败，请稍后重试"));
    },
  });
  const createContactMutation = useMutation({
    mutationFn: createContactWorkbenchContact,
    onSuccess: (detail) => {
      if (detail.vectorSyncWarning) Message.warning({ content: detail.vectorSyncWarning, duration: 8000 });
      else Message.success("联系人已创建");
      void queryClient.invalidateQueries({
        queryKey: [...crmContactWorkbenchQueryRootKey, "contacts"],
      });
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "联系人新增失败，请稍后重试"));
    },
  });

  const createRecordMutation = useMutation({
    mutationFn: createContactWorkbenchRecord,
    onSuccess: (_, variables) => {
      Message.success("联系人动态已添加");
      void Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: [...crmContactWorkbenchQueryRootKey, "contact-records", variables.contactId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...crmContactWorkbenchQueryRootKey, "latest-record-labels"],
        }),
        queryClient.invalidateQueries({
          queryKey: [...crmContactWorkbenchQueryRootKey, "contacts"],
        }),
      ]);
    },
  });

  const deleteRecordMutation = useMutation({
    mutationFn: deleteContactWorkbenchRecord,
    onSuccess: (_, variables) => {
      Message.success("联系人动态已删除");
      void Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: [...crmContactWorkbenchQueryRootKey, "contact-records", variables.contactId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...crmContactWorkbenchQueryRootKey, "contacts"],
        }),
      ]);
    },
  });

  const quickOpportunityCheckMutation = useMutation({
    mutationFn: checkQuickCreateOpportunity,
    onSuccess: (result) => setQuickOpportunityCheck(result),
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "快捷建商机校验失败，请稍后重试"));
    },
  });
  const quickOpportunityCreateMutation = useMutation({
    mutationFn: createQuickOpportunity,
    onSuccess: async () => {
      Message.success("商机已创建");
      await queryClient.invalidateQueries({ queryKey: ["crm_opportunity_workbench"] });
      if (quickOpportunityContactId) {
        await quickOpportunityCheckMutation.mutateAsync(quickOpportunityContactId);
      }
    },
    onError: (error) => {
      Message.error(formatContactWorkbenchError(error, "快捷建商机失败，请稍后重试"));
    },
  });

  const listPage = listQuery.data ?? {
    items: [],
    total: 0,
    page,
    pageSize,
  };
  const contactItems = useMemo(
    () =>
      sortField === "gift_count"
        ? listPage.items
        : listPage.items.map((item) => {
            const giftCount = giftCountsByContactId.get(item.id);
            return giftCount === undefined ? item : { ...item, giftCount };
          }),
    [giftCountsByContactId, listPage.items, sortField],
  );

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(listPage.total / pageSize));
    if (!listQuery.isFetching && page > maxPage) {
      setPage(maxPage);
    }
  }, [listPage.total, listQuery.isFetching, page, pageSize]);

  useEffect(() => {
    const maxGiftPage = Math.max(
      1,
      Math.ceil((giftsQuery.data?.total ?? 0) / giftPageSize),
    );
    if (
      !giftsQuery.isFetching &&
      giftsQuery.data?.page === giftPage &&
      giftPage > maxGiftPage
    ) {
      setGiftPage(maxGiftPage);
    }
  }, [giftPage, giftPageSize, giftsQuery.data, giftsQuery.isFetching]);

  return {
    draftFilters,
    setDraftFilters,
    submittedFilters,
    items: contactItems,
    latestRecordLabels: Object.fromEntries(
      (latestRecordLabelsQuery.data ?? []).map((item) => [item.contactId, item.labelsSnapshot]),
    ) as Record<number, string>,
    latestRecordLabelsLoading: latestRecordLabelsQuery.isFetching,
    giftCountsLoading: giftCountsQuery.isFetching && giftCountsQuery.data === undefined,
    total: listPage.total,
    page,
    pageSize,
    sortField,
    sortDirection,
    selectedContactId,
    quickOpportunityContactId,
    quickOpportunityCheck,
    quickOpportunityLoading:
      quickOpportunityCheckMutation.isPending || quickOpportunityCreateMutation.isPending,
    quickOpportunityCreatingMonth:
      quickOpportunityCreateMutation.isPending
        ? quickOpportunityCreateMutation.variables?.travelMonth
        : undefined,
    selectedContactDetail: detailQuery.data,
    selectedContactRecords: recordsQuery.data ?? [],
    giftContactId,
    giftItems: giftsQuery.data?.items ?? [],
    giftTotal: giftsQuery.data?.total ?? 0,
    giftPage,
    giftPageSize,
    giftOptions: giftOptionsQuery.data ?? [],
    giftLoading: giftsQuery.isLoading,
    giftError: giftsQuery.error,
    giftMutating: createGiftMutation.isPending || updateGiftMutation.isPending || deleteGiftMutation.isPending,
    taskPoolMutating: taskPoolMutation.isPending,
    contactMutating: updateContactMutation.isPending || createContactMutation.isPending,
    recordMutating: createRecordMutation.isPending || deleteRecordMutation.isPending,
    metadata: metadataQuery.data,
    staffOptions: metadataQuery.data?.staffOptions ?? [],
    ownerOptions: ownerOptionsQuery.data,
    ownerOptionsLoading: ownerOptionsQuery.isLoading,
    loading: listQuery.isLoading,
    detailLoading: detailQuery.isLoading || recordsQuery.isLoading,
    mutating:
      transferMutation.isPending ||
      taskPoolMutation.isPending ||
      sharedOwnerMutation.isPending ||
      replaceSharedOwnerMutation.isPending ||
      temporaryTaskHandlerMutation.isPending ||
      temporaryTaskWithdrawMutation.isPending ||
      updateContactMutation.isPending ||
      createContactMutation.isPending ||
      createRecordMutation.isPending ||
      deleteRecordMutation.isPending ||
      createGiftMutation.isPending ||
      updateGiftMutation.isPending ||
      deleteGiftMutation.isPending ||
      quickOpportunityCheckMutation.isPending ||
      quickOpportunityCreateMutation.isPending ||
      nlQueryParseMutation.isPending ||
      schoolBatchAssignmentPreviewMutation.isPending ||
      schoolBatchAssignmentConfirmMutation.isPending,
    nlQueryParsing: nlQueryParseMutation.isPending,
    nlQueryParseResult,
    nlQueryErrorMessage,
    sharedOwnerBatchNotice,
    schoolBatchAssignmentPreviewing: schoolBatchAssignmentPreviewMutation.isPending,
    schoolBatchAssignmentConfirming: schoolBatchAssignmentConfirmMutation.isPending,
    errorMessage:
      listQuery.error || metadataQuery.error
        ? formatContactWorkbenchError(
            listQuery.error ?? metadataQuery.error,
            "联系人工作台数据加载失败，请稍后重试。",
          )
        : "",
    handleViewScopeChange(viewScope: "mine" | "all") {
      setDraftFilters((current) => ({ ...current, viewScope }));
      setSubmittedFilters((current) => ({ ...current, viewScope }));
      setPage(1);
    },
    handleSearch(nextFilters?: ContactWorkbenchListFilters) {
      const source = nextFilters ?? draftFilters;
      if (nextFilters) {
        setDraftFilters(nextFilters);
      }
      setSubmittedFilters(normalizeContactWorkbenchFilters(source));
      setPage(1);
    },
    handleApplyOwnerFilters(nextFilters: {
      ownerUserIds?: number[];
      sharedOwnerUserIds?: number[];
      temporaryHandlerUserIds?: number[];
    }) {
      const normalized = normalizeContactWorkbenchFilters({
        ...submittedFilters,
        ...draftFilters,
        ownerUserIds: nextFilters.ownerUserIds,
        sharedOwnerUserIds: nextFilters.sharedOwnerUserIds,
        temporaryHandlerUserIds: nextFilters.temporaryHandlerUserIds,
        ownerUserId: undefined,
      });
      setDraftFilters(normalized);
      setSubmittedFilters(normalized);
      setPage(1);
    },
    /** 更新智能查询草稿；输入清空时同步清除识别摘要与错误。 */
    handleNlQueryChange(nlQuery: string) {
      setDraftFilters((current) => ({ ...current, nlQuery }));
      if (!nlQuery.trim()) {
        setNlQueryParseResult(null);
        setNlQueryErrorMessage("");
      }
    },
    async handleSmartSearch() {
      const normalizedFilters = normalizeContactWorkbenchFilters(draftFilters);
      setNlQueryErrorMessage("");
      if (!normalizedFilters.nlQuery) {
        setNlQueryParseResult(null);
        setSubmittedFilters(normalizedFilters);
        setPage(1);
        return;
      }
      let parsed: ContactWorkbenchNlQueryParseResult;
      try {
        parsed = await nlQueryParseMutation.mutateAsync(normalizedFilters.nlQuery);
      } catch (error) {
        setNlQueryParseResult(null);
        setNlQueryErrorMessage(
          formatContactWorkbenchError(error, "智能查询解析失败，请稍后重试或改用普通筛选。"),
        );
        return;
      }
      if (!parsed.executable) {
        return;
      }
      setSubmittedFilters(normalizedFilters);
      setPage(1);
    },
    handleReset() {
      const defaults: ContactWorkbenchListFilters = {
        viewScope: submittedFilters.viewScope ?? "mine",
        isActive: true,
      };
      setDraftFilters(defaults);
      setSubmittedFilters(defaults);
      setNlQueryParseResult(null);
      setNlQueryErrorMessage("");
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    handleSort(nextSortField: string, nextSortDirection: "asc" | "desc") {
      setSortField(nextSortField);
      setSortDirection(nextSortDirection);
      setPage(1);
    },
    handleOpenContact(contactId: number) {
      setSelectedContactId(contactId);
    },
    handleCloseContact() {
      setSelectedContactId(undefined);
    },
    async handleOpenQuickOpportunity(contactId: number) {
      setQuickOpportunityContactId(contactId);
      setQuickOpportunityCheck(undefined);
      try {
        await quickOpportunityCheckMutation.mutateAsync(contactId);
      } catch {
        // 错误由 mutation 统一提示，避免事件处理器产生未处理 Promise。
      }
    },
    handleCloseQuickOpportunity() {
      setQuickOpportunityContactId(undefined);
      setQuickOpportunityCheck(undefined);
    },
    handleCreateQuickOpportunity(travelMonth: string) {
      if (!quickOpportunityContactId) return Promise.resolve();
      return quickOpportunityCreateMutation.mutateAsync({
        contactId: quickOpportunityContactId,
        travelMonth,
      });
    },
    handleOpenGifts(contactId: number) {
      setGiftPage(1);
      setGiftContactId(contactId);
    },
    handleCloseGifts() {
      setGiftContactId(undefined);
      setGiftPage(1);
    },
    handleGiftPageChange(nextPage: number) {
      setGiftPage(nextPage);
    },
    handleRefreshGifts() {
      void Promise.all([giftsQuery.refetch(), giftOptionsQuery.refetch()]);
    },
    async handleCreateGift(payload: ContactGiftMutationPayload) {
      if (!giftContactId || createGiftMutation.isPending) return;
      await createGiftMutation.mutateAsync({ contactId: giftContactId, payload });
    },
    async handleUpdateGift(giftId: string, payload: ContactGiftMutationPayload) {
      if (!giftContactId || updateGiftMutation.isPending) return;
      await updateGiftMutation.mutateAsync({
        contactId: giftContactId,
        giftId,
        payload,
      });
    },
    async handleDeleteGift(giftId: string) {
      if (!giftContactId || deleteGiftMutation.isPending) return;
      await deleteGiftMutation.mutateAsync({ contactId: giftContactId, giftId });
    },

    async handleTransferOwner(contactIds: number[], targetUserId: number) {
      await transferMutation.mutateAsync({
        contactIds,
        targetUserId,
        currentUserId,
      });
    },
    handleResolveSelectedContactIds(selection: ContactWorkbenchSelectionResolvePayload) {
      if (selection.mode === "selected_rows") {
        return Promise.resolve(dedupeContactIds(selection.contactIds));
      }
      return fetchAllContactIdsForCurrentQuery(listParams);
    },
    async handleCreateTaskPool(payload: Omit<ContactWorkbenchTaskPoolPayload, "currentUserId">) {
      await taskPoolMutation.mutateAsync({
        ...payload,
        currentUserId,
      });
    },
    async handleAddSharedOwner(contactIds: number[], targetUserId: number) {
      await sharedOwnerMutation.mutateAsync({
        contactIds,
        targetUserIds: [targetUserId],
        currentUserId,
      });
    },
    async handleAddSharedOwners(contactIds: number[], targetUserIds: number[]) {
      await sharedOwnerMutation.mutateAsync({
        contactIds,
        targetUserIds,
        currentUserId,
      });
    },
    async handleReplaceContactSharedOwners(
      contactId: number,
      currentUserIds: number[],
      targetUserIds: number[],
    ) {
      await replaceSharedOwnerMutation.mutateAsync({
        contactId,
        currentUserIds,
        targetUserIds,
        currentUserId,
      });
    },
    async handleUpdateTemporaryTaskHandler(
      contactId: number,
      itemId: number,
      handlerUserId?: number,
    ) {
      await temporaryTaskHandlerMutation.mutateAsync({
        contactId,
        itemId,
        handlerUserId,
      });
    },
    async handleWithdrawTemporaryTask(contactId: number, itemId: number) {
      await temporaryTaskWithdrawMutation.mutateAsync({ contactId, itemId });
    },
    async handleAddSharedOwnersForSelection(
      selection: ContactWorkbenchSelectionResolvePayload,
      targetUserIds: number[],
    ) {
      let contactIds: number[];
      try {
        contactIds =
          selection.mode === "selected_rows"
            ? dedupeContactIds(selection.contactIds)
            : await fetchAllContactIdsForCurrentQuery(listParams);
      } catch (error) {
        Message.error(formatContactWorkbenchError(error, "解析联系人选择范围失败，请稍后重试"));
        return;
      }
      sharedOwnerMutation.mutate({
        contactIds,
        targetUserIds,
        currentUserId,
      });
    },
    async handleUpdateContact(payload: ContactWorkbenchUpdatePayload) {
      if (!selectedContactId) {
        return false;
      }
      try {
        await updateContactMutation.mutateAsync({
          contactId: selectedContactId,
          payload,
        });
        return true;
      } catch {
        return false;
      }
    },
    async handleCreateContact(payload: ContactWorkbenchUpdatePayload) {
      try {
        await createContactMutation.mutateAsync(payload);
        return true;
      } catch {
        return false;
      }
    },
    searchOrganizations(keyword: string) {
      return searchContactWorkbenchOrganizations({
        keyword,
        limit: 50,
      });
    },
    searchSchoolBatchAssignmentOrganizations(keyword: string) {
      return searchContactWorkbenchSchoolBatchAssignmentOrganizations({ keyword, limit: 50 });
    },
    fetchSchoolBatchAssignmentOrganizationScope() {
      return fetchContactWorkbenchSchoolBatchAssignmentOrganizationScope();
    },
    async previewSchoolBatchAssignment(orgIds: number[]): Promise<ContactWorkbenchSchoolBatchAssignmentPreviewResult> {
      return schoolBatchAssignmentPreviewMutation.mutateAsync({ orgIds });
    },
    async confirmSchoolBatchAssignment(
      payload: ContactWorkbenchSchoolBatchAssignmentConfirmPayload,
    ): Promise<ContactWorkbenchSchoolBatchAssignmentConfirmResult> {
      return schoolBatchAssignmentConfirmMutation.mutateAsync(payload);
    },
    searchOrganizationUnits(orgId: number, keyword: string) {
      return searchContactWorkbenchOrganizationUnits({
        orgId,
        keyword,
        limit: 50,
      });
    },
    async handleCreateRecord(payload: ContactWorkbenchRecordCreatePayload) {
      if (!selectedContactId) {
        return;
      }
      await createRecordMutation.mutateAsync({
        contactId: selectedContactId,
        payload: {
          ...payload,
          recordedByUserId: payload.recordedByUserId ?? currentUserId,
        },
      });
    },
    async handleDeleteRecord(recordId: number) {
      if (!selectedContactId) {
        return;
      }
      await deleteRecordMutation.mutateAsync({
        contactId: selectedContactId,
        recordId,
        currentUserId,
      });
    },
  };
}

/** 按当前筛选条件分页拉取全部联系人 ID。 */
async function fetchAllContactIdsForCurrentQuery(
  listParams: ContactWorkbenchListParams,
): Promise<number[]> {
  const contactIds: number[] = [];
  let nextPage = 1;
  let total = Number.POSITIVE_INFINITY;
  while (contactIds.length < total) {
    const pageResult = await fetchContactWorkbenchList({
      ...listParams,
      page: nextPage,
      pageSize: CONTACT_WORKBENCH_ALL_SELECTION_PAGE_SIZE,
    });
    total = pageResult.total;
    contactIds.push(...pageResult.items.map((item) => item.id));
    if (pageResult.items.length === 0) {
      break;
    }
    nextPage += 1;
  }
  return dedupeContactIds(contactIds);
}

/** 按后端单次上限分块转移联系人主销售。 */
async function transferContactsOwnerInChunks(
  payload: ContactWorkbenchTransferPayload,
): Promise<ContactWorkbenchTransferResult> {
  let updatedCount = 0;
  const contactIds = dedupeContactIds(payload.contactIds);
  for (let index = 0; index < contactIds.length; index += CONTACT_WORKBENCH_TRANSFER_CHUNK_SIZE) {
    const result = await transferContactWorkbenchOwner({
      ...payload,
      contactIds: contactIds.slice(index, index + CONTACT_WORKBENCH_TRANSFER_CHUNK_SIZE),
    });
    updatedCount += result.updatedCount;
  }
  return { updatedCount };
}

/** 按目标共享销售和后端单次上限分块追加联系人共享销售。 */
async function addContactsSharedOwnersInChunks({
  contactIds,
  targetUserIds,
  currentUserId,
}: SharedOwnerBatchPayload): Promise<SharedOwnerBatchResult> {
  const dedupedContactIds = dedupeContactIds(contactIds);
  const dedupedTargetUserIds = dedupeUserIds(targetUserIds);
  let updatedCount = 0;
  let skippedCount = 0;
  let logCount = 0;
  for (const targetUserId of dedupedTargetUserIds) {
    for (
      let index = 0;
      index < dedupedContactIds.length;
      index += CONTACT_WORKBENCH_SHARED_OWNER_CHUNK_SIZE
    ) {
      const chunkContactIds = dedupedContactIds.slice(
        index,
        index + CONTACT_WORKBENCH_SHARED_OWNER_CHUNK_SIZE,
      );
      await previewContactAssignment({
        contactIds: chunkContactIds,
        targetUserId,
        relationType: "shared_owner",
        conflictStrategy: "add_shared",
      });
      const result = await confirmContactAssignment({
        contactIds: chunkContactIds,
        targetUserId,
        currentUserId,
        relationType: "shared_owner",
        conflictStrategy: "add_shared",
        permissionLevel: "follow",
        sourceType: "manual",
        reason: "联系人工作台添加共享销售",
      });
      updatedCount += result.updatedCount;
      skippedCount += result.skippedCount;
      logCount += result.logCount;
    }
  }
  return {
    updatedCount,
    skippedCount,
    logCount,
    contactCount: dedupedContactIds.length,
    targetUserCount: dedupedTargetUserIds.length,
  };
}

/** 去重并清洗联系人 ID。 */
/** 按当前选择替换单个联系人的共享销售，新增和撤销分开落库。 */
async function replaceContactSharedOwners({
  contactId,
  currentUserIds,
  targetUserIds,
  currentUserId,
}: SharedOwnerReplacePayload): Promise<{ addedCount: number; removedCount: number }> {
  const currentSet = new Set(dedupeUserIds(currentUserIds));
  const targetSet = new Set(dedupeUserIds(targetUserIds));
  const addUserIds = Array.from(targetSet).filter((userId) => !currentSet.has(userId));
  const removeUserIds = Array.from(currentSet).filter((userId) => !targetSet.has(userId));
  let addedCount = 0;
  let removedCount = 0;
  if (addUserIds.length > 0) {
    const addResult = await addContactsSharedOwnersInChunks({
      contactIds: [contactId],
      targetUserIds: addUserIds,
      currentUserId,
    });
    addedCount += addResult.updatedCount;
  }
  for (const targetUserId of removeUserIds) {
    const removeResult = await revokeContactSharedOwner({
      contactIds: [contactId],
      targetUserId,
      currentUserId,
      reason: "联系人工作台移除共享销售",
    });
    removedCount += removeResult.updatedCount;
  }
  return { addedCount, removedCount };
}

/** 去重并清洗联系人 ID。 */
function dedupeContactIds(contactIds: number[]) {
  return Array.from(new Set(contactIds.filter((contactId) => Number.isFinite(contactId))));
}

/** 去重并清洗员工用户 ID。 */
function dedupeUserIds(userIds: number[]) {
  return Array.from(new Set(userIds.filter((userId) => Number.isFinite(userId))));
}

/** 把已选择联系人投放到协作任务公共池，并立即发布任务。 */
async function createSelectedContactsTaskPool(
  payload: ContactWorkbenchTaskPoolPayload,
): Promise<ContactWorkbenchTaskPoolResult> {
  const result = await createContactWorkbenchCollaborationTask({
    contactIds: payload.contactIds,
    taskType: payload.taskType,
    currentUserId: payload.currentUserId,
    taskName: payload.taskName,
    sourceSnapshot: { source_page: "crm_contact_workbench" },
    remark: "联系人工作台已选联系人投放任务池",
  });
  return {
    taskId: result.taskId,
    insertedCount: result.insertedCount,
    skippedCount: result.skippedCount,
  };
}

/** 格式化联系人工作台可投放的任务池类型。 */
function formatTaskPoolTypeLabel(taskType: ContactWorkbenchTaskPoolPayload["taskType"]) {
  return taskType === "strong_follow" ? "强跟" : "电销";
}
const CONTACT_WORKBENCH_PAGE_SIZE_OPTIONS = new Set([5, 10, 20, 50, 100]);
/** 窄屏联系人列表默认每页条数；桌面仍用配置默认 20。 */
export const CONTACT_WORKBENCH_MOBILE_PAGE_SIZE = 5;
const CONTACT_WORKBENCH_SORT_FIELDS = new Set([
  "contact_name",
  "org_name",
  "record_count",
  "gift_count",
  "last_contact_at",
  "created_at",
  "updated_at",
]);

type ContactWorkbenchUrlState = {
  filters: ContactWorkbenchListFilters;
  page: number;
  pageSize: number;
  sortField: string;
  sortDirection: "asc" | "desc";
};

/** 从当前 URL 读取联系人工作台可分享的查询状态。 */
function readContactWorkbenchUrlState(): ContactWorkbenchUrlState {
  const defaultSortField = crmContactWorkbenchDefaultListParams.sortField ?? "last_contact_at";
  const defaultSortDirection = crmContactWorkbenchDefaultListParams.sortDirection ?? "desc";
  if (typeof window === "undefined") {
    return {
      filters: { viewScope: "mine", isActive: true },
      page: crmContactWorkbenchDefaultListParams.page,
      pageSize: crmContactWorkbenchDefaultListParams.pageSize,
      sortField: defaultSortField,
      sortDirection: defaultSortDirection,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const status = params.get("status");
  const page = parsePositiveInteger(params.get("page"));
  const pageSize = parsePositiveInteger(params.get("pageSize"));
  const sortField = params.get("sort");
  const sortDirection = params.get("direction");
  const contactStarUnset = params.get("starUnset") === "true";
  const contactStar = contactStarUnset ? undefined : readOptionalUrlText(params, "star");

  return {
    filters: normalizeContactWorkbenchFilters({
      viewScope: params.get("viewScope") === "all" ? "all" : "mine",
      keyword: readOptionalUrlText(params, "keyword"),
      ownerUserIds: readUrlPositiveIds(params, "ownerUserIds", "ownerUserId"),
      sharedOwnerUserIds: readUrlPositiveIds(params, "sharedOwnerUserIds", "sharedOwnerUserId"),
      temporaryHandlerUserIds: readUrlPositiveIds(
        params,
        "temporaryHandlerUserIds",
        "temporaryHandlerUserId",
      ),
      orgIds: readUrlPositiveIds(params, "orgIds", "orgId"),
      orgNames: readUrlTexts(params, "orgNames", "orgName"),
      provinces: readUrlTexts(params, "provinces", "province"),
      city: readOptionalUrlText(params, "city"),
      district: readOptionalUrlText(params, "district"),
      isActive: status === "all" ? undefined : status === "inactive" ? false : true,
      nlQuery: readOptionalUrlText(params, "nlQuery"),
      contactStar,
      contactStarUnset,
      recordLabels: readUrlTexts(params, "recordLabels", "recordLabel"),
      followUpLabels: readUrlTexts(params, "followUpLabels", "followUpLabel"),
    }),
    page: page ?? crmContactWorkbenchDefaultListParams.page,
    pageSize:
      pageSize && CONTACT_WORKBENCH_PAGE_SIZE_OPTIONS.has(pageSize)
        ? pageSize
        : crmContactWorkbenchDefaultListParams.pageSize,
    sortField:
      sortField && CONTACT_WORKBENCH_SORT_FIELDS.has(sortField) ? sortField : defaultSortField,
    sortDirection: sortDirection === "asc" || sortDirection === "desc"
      ? sortDirection
      : defaultSortDirection,
  };
}

/** 把联系人工作台查询状态写入 URL，同时保留其他业务参数。 */
function writeContactWorkbenchUrlState(params: ContactWorkbenchListParams) {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  setUrlText(url.searchParams, "viewScope", params.viewScope ?? "mine");
  setUrlText(url.searchParams, "page", String(params.page));
  setUrlText(url.searchParams, "pageSize", String(params.pageSize));
  setUrlText(url.searchParams, "keyword", params.keyword);
  setUrlTexts(url.searchParams, "ownerUserIds", params.ownerUserIds?.map(String));
  url.searchParams.delete("ownerUserId");
  url.searchParams.delete("ownerUserName");
  setUrlTexts(url.searchParams, "sharedOwnerUserIds", params.sharedOwnerUserIds?.map(String));
  setUrlTexts(url.searchParams, "temporaryHandlerUserIds", params.temporaryHandlerUserIds?.map(String));
  setUrlTexts(url.searchParams, "orgIds", params.orgIds?.map(String));
  url.searchParams.delete("orgId");
  setUrlTexts(url.searchParams, "orgNames", params.orgNames);
  url.searchParams.delete("orgName");
  setUrlTexts(url.searchParams, "provinces", params.provinces);
  url.searchParams.delete("province");
  setUrlText(url.searchParams, "city", params.city);
  setUrlText(url.searchParams, "district", params.district);
  setUrlText(url.searchParams, "nlQuery", params.nlQuery);
  setUrlText(url.searchParams, "star", params.contactStar);
  setUrlText(url.searchParams, "starUnset", params.contactStarUnset ? "true" : undefined);
  setUrlTexts(url.searchParams, "recordLabels", params.recordLabels);
  url.searchParams.delete("recordLabel");
  setUrlTexts(url.searchParams, "followUpLabels", params.followUpLabels);
  url.searchParams.delete("followUpLabel");
  setUrlText(
    url.searchParams,
    "status",
    params.isActive === undefined ? "all" : params.isActive ? "active" : "inactive",
  );
  setUrlText(url.searchParams, "sort", params.sortField);
  setUrlText(url.searchParams, "direction", params.sortDirection);
  window.history.replaceState(window.history.state, "", url);
}

/** 解析正整数 URL 参数。 */
function parsePositiveInteger(value: string | null) {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/** 读取重复 URL 正整数参数，并兼容旧单值参数。 */
function readUrlPositiveIds(params: URLSearchParams, key: string, legacyKey: string) {
  const values = [...params.getAll(key), ...(params.get(legacyKey) ? [params.get(legacyKey)!] : [])];
  const normalized: number[] = [];
  for (const value of values) {
    const parsed = parsePositiveInteger(value);
    if (parsed && !normalized.includes(parsed)) {
      normalized.push(parsed);
    }
  }
  return normalized.length ? normalized : undefined;
}

/** 读取并清洗可选 URL 文本。 */
function readOptionalUrlText(params: URLSearchParams, key: string) {
  const value = params.get(key)?.trim();
  return value || undefined;
}

/** 读取重复 URL 文本参数，并兼容旧单值参数。 */
function readUrlTexts(params: URLSearchParams, key: string, legacyKey: string) {
  const values = params.getAll(key);
  const legacyValue = readOptionalUrlText(params, legacyKey);
  if (legacyValue) values.push(legacyValue);
  const normalized = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
  return normalized.length ? normalized : undefined;
}

/** 设置或移除单个 URL 文本参数。 */
function setUrlText(params: URLSearchParams, key: string, value?: string) {
  if (value) {
    params.set(key, value);
    return;
  }
  params.delete(key);
}

/** 用重复参数写入多值 URL 状态。 */
function setUrlTexts(params: URLSearchParams, key: string, values?: string[]) {
  params.delete(key);
  values?.forEach((value) => params.append(key, value));
}
